import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useFeatureGate } from '@statsig/react-bindings';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  /** The user's theme preference (light, dark, or system) */
  mode: ThemeMode;
  /** The resolved theme after considering system preference */
  theme: ResolvedTheme;
  /** Set the theme mode */
  setMode: (mode: ThemeMode) => void;
  /** Toggle between light and dark (ignores system preference) */
  toggle: () => void;
  /** Whether dark mode feature is enabled via Statsig gate */
  darkModeEnabled: boolean;
}

const THEME_STORAGE_KEY = 'butlr-theme-mode';

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') {
    return getSystemTheme();
  }
  return mode;
}

function applyTheme(theme: ResolvedTheme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { value: darkModeEnabled } = useFeatureGate('dark_mode');

  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'light';
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    } catch {
      // localStorage can throw in Safari private browsing or restricted iframes
    }
    return 'light';
  });

  // When gate is off, force light mode regardless of stored preference
  const effectiveMode = darkModeEnabled ? mode : 'light';
  const [theme, setTheme] = useState<ResolvedTheme>(() => resolveTheme(effectiveMode));

  useEffect(() => {
    const resolved = resolveTheme(effectiveMode);
    setTheme(resolved);
    applyTheme(resolved);
    if (darkModeEnabled) {
      try {
        localStorage.setItem(THEME_STORAGE_KEY, mode);
      } catch {
        // localStorage can throw in Safari private browsing or restricted iframes
      }
    }
  }, [effectiveMode, darkModeEnabled, mode]);

  useEffect(() => {
    if (effectiveMode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const resolved = resolveTheme(effectiveMode);
      setTheme(resolved);
      applyTheme(resolved);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [effectiveMode]);

  useEffect(() => {
    applyTheme(theme);
  }, []);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
  }, []);

  const toggle = useCallback(() => {
    setModeState((current) => {
      const currentResolved = resolveTheme(current);
      return currentResolved === 'light' ? 'dark' : 'light';
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, theme, setMode, toggle, darkModeEnabled }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export function useIsDarkMode(): boolean {
  const { theme } = useTheme();
  return theme === 'dark';
}
